import React, { useContext, useMemo, useState } from 'react';
import styled from 'styled-components';

import { compositeForContentType } from 'components/Studio/canvasMapping/dateSettings';
import CompositeCard from 'components/Studio/CompositeCard';
import { getValueFontSize } from 'components/Studio/constants';
import { DateScope } from 'components/Studio/dateSettings';
import { InheritedCanvasFilters } from 'components/Studio/filterSettings';
import FullscreenOverlay, {
  FULLSCREEN_LAYOUT,
  getFullscreenValueFontSize,
} from 'components/Studio/FullscreenOverlay';
import useElementSize from 'components/Studio/useElementSize';
import DateInputProvider from 'contextProviders/DateInputProvider';
import DateInputContext from 'contexts/DateInputContext';
import ReportContext from 'contexts/ReportContext';

import CompositeMenuButtons from './CompositeMenuButtons';
import GadgetDateButton from './GadgetDateButton';

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  height: 100%;
`;

const noop = () => {};
const applyNow = (apply: () => void) => apply();

// On a dashboard, a Composite has dates of its own for the view, as a
// classic gadget does: its saved ones, else the dashboard's, which its date
// button changes without saving (or touching the dashboard's). Starts again
// from them whenever they change, handing them to its children as the dates
// the view's are compared with.
const DashboardDateScope = ({
  dateSettings,
  children,
}: {
  dateSettings: DateFiltered | undefined;
  children: (seed: DateFiltered) => JSX.Element;
}) => {
  const inherited = useContext(DateInputContext);
  const hasOwnRange =
    !!dateSettings &&
    !!(
      dateSettings.dateRange ||
      dateSettings.relativeDateRange ||
      dateSettings.advancedRelativeDateRange
    );
  // Its own range, else the dashboard's (with its own date field, if set)
  const seed: DateFiltered =
    dateSettings && hasOwnRange
      ? dateSettings
      : {
          dateField: dateSettings
            ? dateSettings.dateField
            : inherited.dateField,
          dateRange: inherited.dateRange,
          relativeDateRange: inherited.relativeDateRange,
          advancedRelativeDateRange: inherited.advancedRelativeDateRange,
        };
  return (
    <DateInputProvider
      key={JSON.stringify(seed)}
      initialDateField={seed.dateField}
      initialDateRange={seed.dateRange}
      initialRelativeDateRange={seed.relativeDateRange}
      initialAdvancedRelativeDateRange={seed.advancedRelativeDateRange}
    >
      {children(seed)}
    </DateInputProvider>
  );
};

// A Studio Composite block on a dashboard or report, view-only. It has no
// gadget doc, so no gadget providers scope it: its elements take the
// dashboard's or report's filters (and run-time filters) ahead of the
// block's and their own, as Studio's canvas filters are. On a report it
// follows the report's range, keeping only its own date fields. Its header
// sets its own filters; on a dashboard its dates for the view open from a
// calendar icon beside them. Its charts act on a click as a gadget's do: on
// a dashboard they open the chart menu, on a report they drill the report.
// A fullscreen icon opens it over the whole window, in the same scopes.
const StudioCompositeCardView = ({
  card,
}: {
  card: CanvasCard.StudioCompositeCard;
}) => {
  const { report } = useContext(ReportContext);
  const { ref, width, height } = useElementSize<HTMLDivElement>();
  const { i, x, y, w, h } = card.layout;
  const { composite } = card.content;
  const contentType: Studio.ContentType = report ? 'report' : 'dashboard';

  const studioCard = useMemo(
    (): Studio.Card => ({
      id: i,
      visType: 'composite',
      ...compositeForContentType({ composite, contentType }),
      layout: { x, y, w, h },
    }),
    [composite, contentType, h, i, w, x, y],
  );

  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  const renderCard = ({
    layout,
    valueFontSize,
    viewMenu,
  }: {
    layout: Studio.Card['layout'];
    valueFontSize: number;
    viewMenu?: JSX.Element;
  }) => (
    <CompositeCard
      card={{ ...studioCard, layout }}
      isSelected={false}
      isDropTarget={false}
      valueFontSize={valueFontSize}
      selectedElementId={undefined}
      selectOnRelease={applyNow}
      onSelectElement={noop}
      onSelectBlock={noop}
      onRowsChange={noop}
      isEmbedded
      hasChartMenu={!report}
      viewMenu={viewMenu}
    />
  );

  const renderBody = (datesButton?: JSX.Element) => (
    <>
      {renderCard({
        layout: studioCard.layout,
        valueFontSize: getValueFontSize({ width, height }),
        viewMenu: (
          <CompositeMenuButtons
            card={card}
            datesButton={datesButton}
            onFullscreen={() => setIsFullscreen(true)}
          />
        ),
      })}
      {isFullscreen && (
        <FullscreenOverlay onClose={() => setIsFullscreen(false)}>
          {renderCard({
            layout: FULLSCREEN_LAYOUT,
            valueFontSize: getFullscreenValueFontSize(),
          })}
        </FullscreenOverlay>
      )}
    </>
  );

  return (
    <Wrapper ref={ref} data-testid={`studio-composite-${i}`}>
      <InheritedCanvasFilters isDrillable={!!report}>
        {report ? (
          <DateScope dateSettings={studioCard.dateSettings}>
            {renderBody()}
          </DateScope>
        ) : (
          <DashboardDateScope dateSettings={studioCard.dateSettings}>
            {(seed) =>
              renderBody(<GadgetDateButton savedDateSettings={seed} />)
            }
          </DashboardDateScope>
        )}
      </InheritedCanvasFilters>
    </Wrapper>
  );
};

export default StudioCompositeCardView;
